/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { formatNumber } from '../../../../utilities/formatHelpers';
import { currencies, exchange, walletTypes } from '../../../../utilities/constants';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { exchangeScreensFlow, exchangeTypes } from '../../constants';
import {
  BUY_EXCHANGE,
  CONVERT_EXCHANGE,
  MEXC_BUY_ESTIMATION,
  MEXC_SELL_ESTIMATION,
  SELL_EXCHANGE,
  USER_CREATE_WALLET,
} from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import { useUserWallets } from '../../hooks';
import { WALLETS_ALL } from '../../queries';
import { useLedgerFilteredWallets } from '../../../../utilities/sharedHooks/ledgerWallets';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { GenericLoading } from '../../../../components/Placeholders';
import Alert from '../../../../components/Alert';

const ConfirmExchange = ({
  openScreen,
  form,
  hide,
  forcedDestinationWallet,
  isUserAccountPage,
}) => {
  const { t: translate } = useTranslation();
  const [, refetchUserWallets] = useUserWallets();

  const history = useHistory();

  const [priceEstimation, setPriceEstimation] = useState('');
  const [sourceEstimatedAmount, setSourceEstimatedAmount] = useState(0);
  const [destinationEstimatedAmount, setDestinationEstimatedAmount] = useState(0);
  const [showFeeTransparencySwapIframe, setShowFeeTransparencySwapIframe] = useState(false);

  const openIframe = () => {
    setShowFeeTransparencySwapIframe(true);
  };

  const sourceDecmialPoints =
    form.source.currency_type === walletTypes.FIAT ? exchange.FIAT_VALUE : exchange.CRYPTO_VALUE;

  const destinationDecmialPoints =
    form.destination.currency_type === walletTypes.FIAT
      ? exchange.FIAT_VALUE
      : exchange.CRYPTO_VALUE;

  const destinationCurrencyCode = form?.destination?.currency_code;
  const sourceCurrencyCode = form?.source?.currency_code;

  const [enableThirdPartyExchange] = useSetting(
    'app_features',
    'feature_enable_third_party_exchange'
  );

  const [priceEstimationHoldTime] = useSetting(
    'server_configurations',
    'exchange_price_estimation_hold_time'
  );

  const [timer, setTimer] = useState(0);

  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      if (timer > 0) setTimer(timer - 1);
    }, 1000);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [timer]);

  const isBXXSourceOrDestination =
    (destinationCurrencyCode === 'bxx' || sourceCurrencyCode === 'bxx') && enableThirdPartyExchange;

  const sourceCurrencyCodeForRate = useMemo(() => {
    if (isBXXSourceOrDestination && sourceCurrencyCode === 'bxx') {
      return sourceCurrencyCode;
    }
    if (isBXXSourceOrDestination && destinationCurrencyCode === 'bxx') {
      return destinationCurrencyCode;
    }
    return form?.source?.currency_code;
  }, [
    isBXXSourceOrDestination,
    sourceCurrencyCode,
    destinationCurrencyCode,
    form?.source?.currency_code,
  ]);

  const destinationCurrencyCodeForRate = useMemo(() => {
    if (isBXXSourceOrDestination && sourceCurrencyCode === 'bxx') {
      return destinationCurrencyCode;
    }
    if (isBXXSourceOrDestination && destinationCurrencyCode === 'bxx') {
      return sourceCurrencyCode;
    }
    return form?.destination?.currency_code;
  }, [
    isBXXSourceOrDestination,
    sourceCurrencyCode,
    destinationCurrencyCode,
    form?.destination?.currency_code,
  ]);

  const conversionRate = useRate(sourceCurrencyCodeForRate, destinationCurrencyCodeForRate);

  // in case the exchange is from fiat to crypto only
  const showLegalNote =
    (form.source.currency_type === walletTypes.FIAT || !!form.source.fiat_currency_peg) &&
    (form.destination.currency_type === walletTypes.CRYPTO ||
      form.destination.currency_type === walletTypes.INTERNAL);

  const priceImpactValue = useMemo(() => {
    if (sourceCurrencyCode === 'bxx' && priceEstimation && conversionRate) {
      return (
        (Math.abs(priceEstimation - conversionRate).toFixed(exchange.FIAT_LESS_ONE_VALUE) /
          conversionRate) *
        100
      );
    }
    if (destinationCurrencyCode === 'bxx' && priceEstimation && conversionRate) {
      return (
        (Math.abs(conversionRate - priceEstimation).toFixed(exchange.FIAT_LESS_ONE_VALUE) /
          conversionRate) *
        100
      );
    }

    return 0;
  }, [conversionRate, priceEstimation, sourceCurrencyCode, destinationCurrencyCode]);

  const exchangeType = useMemo(() => {
    if (
      form.source.currency_type === walletTypes.FIAT &&
      (form.destination.currency_type === walletTypes.CRYPTO ||
        form.destination.currency_type === walletTypes.INTERNAL)
    ) {
      return exchangeTypes.BUY;
    }
    if (
      (form.source.currency_type === walletTypes.CRYPTO ||
        form.source.currency_type === walletTypes.INTERNAL) &&
      form.destination.currency_type === walletTypes.FIAT
    ) {
      return exchangeTypes.SELL;
    }
    return exchangeTypes.CONVERT;
  }, [form.source, form.destination]);

  const { mutate: MEXCSellEstimation, isLoading: isMEXCSellEstimationLoading } = useMutation(
    MEXC_SELL_ESTIMATION,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (data) {
          setPriceEstimation(data?.priceEstimation);
          setTimer(priceEstimationHoldTime);
          setSourceEstimatedAmount(data?.baseQuantity);
          setDestinationEstimatedAmount(data?.quoteQuantityEstimation);
        }
      },
    }
  );

  const { mutate: MEXCBuyEstimation, isLoading: isMEXCBuyEstimationLoading } = useMutation(
    MEXC_BUY_ESTIMATION,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (data) {
          setPriceEstimation(data?.priceEstimation);
          setTimer(priceEstimationHoldTime);
          setSourceEstimatedAmount(data?.baseQuantity);
          setDestinationEstimatedAmount(data?.quoteQuantityEstimation);
        }
      },
    }
  );

  useEffect(() => {
    if (timer === 0 && isBXXSourceOrDestination) {
      if (sourceCurrencyCode === 'bxx') {
        MEXCSellEstimation({
          source: sourceCurrencyCode?.toUpperCase(),
          destination: destinationCurrencyCode?.toUpperCase(),
          baseAmount: form?.sourceAmount,
        });
      } else if (destinationCurrencyCode === 'bxx') {
        MEXCBuyEstimation({
          source: sourceCurrencyCode?.toUpperCase(),
          destination: destinationCurrencyCode?.toUpperCase(),
          baseAmount: form?.sourceAmount,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exchangeMutationKey = useMemo(() => {
    if (exchangeType === exchangeTypes.BUY) {
      return BUY_EXCHANGE;
    }
    if (exchangeType === exchangeTypes.SELL) {
      return SELL_EXCHANGE;
    }
    return CONVERT_EXCHANGE;
  }, [exchangeType]);

  const { mutate: exchangeMutate, isLoading: isLoadingExchange } = useMutation(
    exchangeMutationKey,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          if (isUserAccountPage) refetchUserWallets();
          showAlert({
            message: translate('confirm_exchange_success_msg')
              .replace(
                '%s',
                `${formatNumber(
                  form.sourceAmount,
                  sourceDecmialPoints
                )} ${form.source.currency_code.toUpperCase()}`
              )
              .replace(
                '%d',
                `${formatNumber(
                  form.destinationAmount,
                  destinationDecmialPoints
                )} ${form.destination.currency_code.toUpperCase()}`
              ),
            type: 'success',
          });
          hide();
          if (forcedDestinationWallet) {
            history.push('/dashboard/staking');
          }
        }
      },
    }
  );

  const { refetch: refetchAllWallets, isLoading: isAllWalletsLoading } = useLedgerFilteredWallets(
    WALLETS_ALL,
    {
      onSettled(data, error) {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          const wallet = data.find((w) => w.coin === form.destination.currency_code);
          if (exchangeType === exchangeTypes.BUY) {
            exchangeMutate({
              source: form.sourceWallet.address,
              recipient: wallet.address,
              crypto_amount: formatNumber(form.destinationAmount, exchange.CRYPTO_VALUE),
              fiat_amount: formatNumber(form.sourceAmount, exchange.FIAT_VALUE),
              toCoin: form.destination.currency_code,
            });
          } else if (exchangeType === exchangeTypes.SELL) {
            exchangeMutate({
              source: form.sourceWallet.address,
              recipient: wallet.address,
              crypto_amount: formatNumber(form.sourceAmount, exchange.CRYPTO_VALUE),
              fiat_amount: formatNumber(form.destinationAmount, exchange.FIAT_VALUE),
              coin: form.sourceWallet.coin,
            });
          } else {
            exchangeMutate({
              sourceCoin: form.source.currency_code,
              destinationCoin: form.destination.currency_code,
              sourceAmount: formatNumber(form.sourceAmount, exchange.CRYPTO_VALUE),
            });
          }
        }
      },
    }
  );

  const { mutate: createWallets, isLoading: isCreatingWallets } = useMutation(USER_CREATE_WALLET, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else if (data[form.destination.currency_code]) {
        const { error: walletError } = data[form.destination.currency_code];
        if (walletError) {
          if (walletError.error) {
            showAlert({ message: walletError.error.message, type: 'danger' });
          } else {
            showAlert({ message: walletError, type: 'danger' });
          }
        } else {
          refetchAllWallets();
        }
      }
    },
  });

  const confirmTransaction = () => {
    if (form.sourceWallet) {
      let _sourceAmount;
      let _destinationAmount;
      if (exchangeType === exchangeTypes.BUY) {
        _sourceAmount = formatNumber(form.sourceAmount, exchange.FIAT_VALUE);
        _destinationAmount = formatNumber(form.destinationAmount, exchange.CRYPTO_VALUE);

        if (!form.destinationWallet) {
          createWallets({ wallets: [form.destination.currency_code] });
        } else {
          exchangeMutate({
            source: form.sourceWallet.address,
            recipient: form.destinationWallet.address,
            crypto_amount: _destinationAmount,
            fiat_amount: _sourceAmount,
            toCoin: form.destination.currency_code,
          });
        }
      } else if (exchangeType === exchangeTypes.SELL) {
        _sourceAmount = formatNumber(form.sourceAmount, exchange.CRYPTO_VALUE);
        _destinationAmount = formatNumber(form.destinationAmount, exchange.FIAT_VALUE);
        if (!form.destinationWallet) {
          createWallets({ wallets: [form.destination.currency_code] });
        } else {
          exchangeMutate({
            source: form.sourceWallet.address,
            recipient: form.destinationWallet.address,
            crypto_amount: _sourceAmount,
            fiat_amount: _destinationAmount,
            coin: form.sourceWallet.coin,
          });
        }
      } else {
        _sourceAmount = formatNumber(form.sourceAmount, exchange.CRYPTO_VALUE);
        if (!form.destinationWallet) {
          createWallets({ wallets: [form.destination.currency_code] });
        } else {
          exchangeMutate({
            sourceCoin: form.source.currency_code,
            destinationCoin: form.destination.currency_code,
            sourceAmount: _sourceAmount,
          });
        }
      }
    }
  };

  const requestNewQuote = () => {
    if (sourceCurrencyCode === 'bxx') {
      MEXCSellEstimation({
        source: sourceCurrencyCode?.toUpperCase(),
        destination: destinationCurrencyCode?.toUpperCase(),
        baseAmount: form?.sourceAmount,
      });
    } else if (destinationCurrencyCode === 'bxx') {
      MEXCBuyEstimation({
        source: sourceCurrencyCode?.toUpperCase(),
        destination: destinationCurrencyCode?.toUpperCase(),
        baseAmount: form?.sourceAmount,
      });
    }
  };

  const renderFooterBtnLabel = useMemo(() => {
    if (isBXXSourceOrDestination && timer > 0) {
      return translate('confirm_exchange_timer_label').replace('__TIMER__', timer);
    }
    if (isBXXSourceOrDestination && timer === 0) {
      return translate('exchange_request_new_quote_btn_label');
    }
    return translate('confirm_button_label');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBXXSourceOrDestination, timer]);

  const renderFooter = () => (
    <button
      className="btn btn-primary py-3"
      loading={
        isCreatingWallets ||
        isAllWalletsLoading ||
        isLoadingExchange ||
        isMEXCSellEstimationLoading ||
        isMEXCBuyEstimationLoading
          ? 'true'
          : 'false'
      }
      type="button"
      onClick={
        !isBXXSourceOrDestination || (isBXXSourceOrDestination && timer > 0)
          ? confirmTransaction
          : requestNewQuote
      }>
      {renderFooterBtnLabel}
    </button>
  );

  const youExchangeValue = useMemo(() => {
    if (isBXXSourceOrDestination) {
      return formatNumber(sourceEstimatedAmount, sourceDecmialPoints);
    }
    return formatNumber(form.sourceAmount, sourceDecmialPoints);
  }, [isBXXSourceOrDestination, form?.sourceAmount, sourceEstimatedAmount, sourceDecmialPoints]);

  const youGetValue = useMemo(() => {
    if (isBXXSourceOrDestination) {
      return formatNumber(destinationEstimatedAmount, destinationDecmialPoints);
    }
    return formatNumber(form.destinationAmount, destinationDecmialPoints);
  }, [
    isBXXSourceOrDestination,
    form?.destinationAmount,
    destinationEstimatedAmount,
    destinationDecmialPoints,
  ]);

  const rateDisplayValue = useMemo(() => {
    if (isBXXSourceOrDestination) {
      return `1 BXX ≈ ${formatNumber(priceEstimation, exchange.FIAT_LESS_ONE_VALUE)} ${
        sourceCurrencyCode === 'bxx'
          ? destinationCurrencyCode?.toUpperCase()
          : sourceCurrencyCode?.toUpperCase()
      }`;
    }
    return `1 ${form.source.currency_code.toUpperCase()} ≈ ${formatNumber(
      conversionRate,
      currencies[form.destination.currency_code.toUpperCase()].decimal
    )} ${form.destination.currency_code.toUpperCase()}`;
  }, [
    isBXXSourceOrDestination,
    priceEstimation,
    form,
    conversionRate,
    destinationCurrencyCode,
    sourceCurrencyCode,
  ]);

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() => openScreen(exchangeScreensFlow.MAIN)}
      renderFooter={renderFooter}
      title={translate('confirm_exchange_title')}>
      {isBXXSourceOrDestination && (isMEXCBuyEstimationLoading || isMEXCSellEstimationLoading) && (
        <GenericLoading isFullScreen={false} customSpacing="h-100" />
      )}
      {(!isBXXSourceOrDestination ||
        (isBXXSourceOrDestination &&
          !isMEXCBuyEstimationLoading &&
          !isMEXCSellEstimationLoading)) && (
        <>
          <div className="rounded-extra bg-light mt-3 card-calculator__summary">
            <div className="d-flex flex-column p-0 mb-2">
              <div className="p-3">
                <div className="d-flex flex-row justify-content-between">
                  <p className="card-calculator__summary--item-note text-shadow text-f6">
                    {translate('you_exchange_label')}
                  </p>
                  <b className="text-f6">
                    {`${youExchangeValue} ${form.source.currency_code.toUpperCase()}`}
                  </b>
                </div>
              </div>
              <div className="p-3">
                <div className="d-flex flex-row justify-content-between">
                  <p className="card-calculator__summary--item-note text-shadow text-f6">
                    {translate('you_get_estimated_label')}
                  </p>
                  <b className="text-f6">
                    {`${youGetValue} ${form.destination.currency_code.toUpperCase()}`}
                  </b>
                </div>
              </div>
              <div className="p-3">
                <div className="d-flex flex-row justify-content-between">
                  <p className="card-calculator__summary--item-note text-shadow text-f6">
                    {isBXXSourceOrDestination
                      ? translate('confirm_exchange_rate_mexc_label')
                      : translate('confirm_exchange_rate_label')}
                  </p>
                  <b className="text-f6">{rateDisplayValue}</b>
                </div>
              </div>
              {isBXXSourceOrDestination && (
                <div className="p-3">
                  <div className="d-flex flex-row justify-content-between">
                    <p className="card-calculator__summary--item-note text-shadow text-f6">
                      {translate('confirm_exchange_mexc_price_impact')}
                    </p>
                    <b className="text-f6">{`${formatNumber(
                      priceImpactValue,
                      exchange.FIAT_VALUE
                    )}%`}</b>
                  </div>
                </div>
              )}
            </div>
          </div>
          {!isBXXSourceOrDestination && (
            <div className="d-flex flex-column">
              <p className="text-f6 text-shadow">{translate('confirm_exchange_rate_note')}</p>
              <p className="text-f6 text-shadow mt-2">
                {translate('confirm_exchange_rate_confirmation_note')}
              </p>
              <p className="text-f6 text-shadow mt-2">
                {translate('confirm_exchange_rate_link_description')}
                <span
                  className="text-primary px-1 cursor-pointer"
                  role="presentation"
                  onClick={openIframe}
                  onKeyDown={openIframe}>
                  {translate('confirm_exchange_rate_link')}
                </span>
              </p>
            </div>
          )}

          {showLegalNote && (
            <div className="mt-3">
              <Alert icon="info" type="info">
                <p className="w-100 text-f6 text-dark">
                  <Trans
                    i18nKey="exchange_legal_note"
                    values={{
                      learn_more: translate('learn_more'),
                    }}
                    components={[
                      // eslint-disable-next-line jsx-a11y/anchor-has-content,jsx-a11y/control-has-associated-label
                      <a
                        key="learn-more"
                        className="text-decoration-none text-primary text-t6"
                        href="https://withcl.com/faq/"
                        rel="noreferrer"
                        target="_blank"
                      />,
                    ]}
                  />
                </p>
              </Alert>
            </div>
          )}

          {isBXXSourceOrDestination && (
            <div className="d-flex flex-column">
              <p className="text-f6 text-shadow mt-2">
                {translate('confirm_exchange_mexc_note_1')}
              </p>
              <p className="text-f6 text-shadow mt-2">
                {translate('confirm_exchange_mexc_note_2')}
              </p>
              <p className="text-f6 text-shadow mt-2">
                {translate('confirm_exchange_mexc_note_3')}
              </p>

              <p className="text-f6 text-shadow mt-3">
                {translate('confirm_exchange_mexc_note_4')}
              </p>
              <p className="text-f6 text-shadow mt-2">
                {translate('confirm_exchange_mexc_note_5')}
              </p>
            </div>
          )}
        </>
      )}
      {showFeeTransparencySwapIframe && (
        <div className="w-100 h-100 position-fixed start-0 top-0 bg-light" style={{ zIndex: 999 }}>
          <span>
            <i
              onClick={() => {
                setShowFeeTransparencySwapIframe(false);
              }}
              onKeyDown={() => {}}
              role="button"
              className="close-btn p-2 bg-shadow position-absolute end-0 top-0 cursor-pointer icon icon-close-other text-white"
              tabIndex={-1}
              aria-label="Close"
            />
          </span>
          <iframe src="https://withcl.com/legal" title="Waiting list" width="100%" height="100%" />
        </div>
      )}
    </ModalBodyLayout>
  );
};

export default ConfirmExchange;

ConfirmExchange.propTypes = {
  openScreen: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  hide: PropTypes.func.isRequired,
  forcedDestinationWallet: PropTypes.object,
  isUserAccountPage: PropTypes.bool,
};

ConfirmExchange.defaultProps = {
  forcedDestinationWallet: null,
  isUserAccountPage: false,
};
